import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ErrorResponse } from 'store/types';

export const isFetchBaseQueryError = (
  error: unknown
): error is FetchBaseQueryError & { data: NonNullable<FetchBaseQueryError['data']> } => {
  return typeof error === 'object' && error != null && 'data' in error && error.data !== null;
};

export const isFetchBaseQueryErrorWithoutData = (
  error: unknown
): error is { status: number | string; data: null } => {
  return (
    typeof error === 'object' && error != null && 'data' in error && error.data == null && 'status' in error
  );
};

export const isCustomFetchError = (
  error: unknown
): error is Extract<FetchBaseQueryError, { status: string }> => {
  return typeof error === 'object' && error != null && 'status' in error && typeof error.status === 'string';
};

export const isAbortError = (error: unknown): error is { name: 'AbortError'; message: string } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as Record<string, unknown>).name === 'AbortError'
  );
};

export const getResponseError = (error: unknown) => {
  // Сырой AbortError из-за бага RTK Query #2915
  if (isAbortError(error)) {
    return 'Превышено время ожидания ответа сервиса. Попробуйте позже.';
  }
  if (isCustomFetchError(error)) {
    switch (error.status) {
      case 'FETCH_ERROR':
        return 'Сервис временно недоступен. Проверьте соединение или попробуйте позже.';
      case 'TIMEOUT_ERROR':
        return 'Превышено время ожидания ответа сервиса. Попробуйте снова.';
      case 'PARSING_ERROR':
      case 'CUSTOM_ERROR':
        return 'Произошла непредвиденная ошибка сервиса.';
    }
  }
  if (isFetchBaseQueryErrorWithoutData(error)) {
    return `Произошла ошибка, код: ${error.status}`;
  }
  if (isFetchBaseQueryError(error)) {
    const data = error.data as ErrorResponse;
    if (data.status === 500 && data.title !== undefined) {
      return data.title;
    }
    if (data.detail !== undefined) {
      return data.detail;
    }
    if (data.errors) {
      // Случай 1: errors — простой массив строк
      if (Array.isArray(data.errors)) {
        return data.errors.join(', ');
      }
      // Случай 2: errors — объект { field: ["msg1", "msg2"], ... }
      if (typeof data.errors === 'object') {
        const messages = Object.values(data.errors)
          .filter((v): v is string[] => Array.isArray(v))
          .flat();
        return messages.length > 0 ? messages.join(', ') : 'Произошла ошибка';
      }
    }
  }
  return 'Произошла ошибка';
};

export const getResponseTraceId = (error: unknown): string | undefined => {
  if (!isFetchBaseQueryError(error)) {
    return undefined;
  }
  const data = error.data as ErrorResponse;
  return data.traceId;
};
